import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Input,
  Label,
  Button,
} from "reactstrap";
import { APIS_LIST, ITemplateAttributesModel, KB_NAMES_ENUM } from "../../models/ChatModels";
import { getFromSession, text_convert_lower } from "../../helpers/GenericHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faChevronUp, faCopy, faMound, faXmark } from "@fortawesome/free-solid-svg-icons";
import { IAPIPromptsModel, IProjectModel, ThemeTypes } from "../../models/SharedModel";
import { ProjectStatus } from "../../models/ProjectModel";
import { APP_Config } from "../../helpers/AppConfig";
import { copyFile } from "fs";
import { ThemeContext } from "../../models/ThemeContext";

interface IApiListModalProps {
  apiModal: boolean;
  toggleAPIModal: () => void;
  handleSuccess: Function;
  handleCancel: Function;
  apisList: IAPIPromptsModel[];
  promptsList: IAPIPromptsModel[];
  attributes?: ITemplateAttributesModel[];
  status: ProjectStatus;
  promtStatus: any;
  selectedProject?: IProjectModel;
}

const ApiModal = (props: IApiListModalProps) => {
  const {
    apiModal,
    toggleAPIModal,
    handleSuccess,
    handleCancel,
    apisList,
    promptsList,
    attributes,
    status,
    promtStatus,
    selectedProject
  } = props;

  // states
  const [selectedPrompts, setSelectedPrompts] = useState<IAPIPromptsModel[]>([]);
  const [selectedApis, setSelectedApis] = useState<IAPIPromptsModel[]>([]);
  const [isCopied, setIsCopied] = useState(false);

  const basicUrl = useMemo(() => 
    `${APP_Config()?.REACT_APP_DOCUMENT_AI_URL}/${selectedProject?.project_name}/${selectedProject?.version}`, [selectedProject])

  // helpers
  const handleSwitch = (value: IAPIPromptsModel) =>
    setSelectedApis((prev) =>
      prev?.map((each) =>
        each?.type === value?.type ? { ...each, status: !each?.status } : each
      )
    );

  const handlePromptSelection = (value: IAPIPromptsModel) =>
    setSelectedPrompts((prev) =>
      prev?.map((each) =>
        each?.prompt === value?.prompt
          ? { ...each, status: !each?.status }
          : each
      )
    );

  const handleSubmit = () => {
    handleSuccess({
      promt_status: [
        ...selectedApis?.map(each => ({ [each?.type]: each?.prompt, status: each?.status })),
        { chatbot_prompts: [
          ...selectedPrompts?.map((each, index) => ({
            [`prompt${index + 1}`]: each?.prompt, status: each?.status
          }))
        ] }
      ]
    });
  };

  const disableCheck = (value: IAPIPromptsModel) => {
    if (value?.type === APIS_LIST.DATA_EXTRACTION) {
      if (attributes && attributes?.length > 0) {
        return false
      } else return true
    }
    else {
      if (value?.prompt && value?.prompt?.length > 0) {
        return false
      } else return  true
    }
  }

  // use effect
  useEffect(() => setSelectedApis(apisList), [apisList])

  useEffect(() => setSelectedPrompts(promptsList), [promptsList])

  const getError = (value: IAPIPromptsModel) => {
    if (value?.type === APIS_LIST.DATA_EXTRACTION) {
      if (attributes && attributes?.length > 0) {
        return <></>
      } else return (<div className="error-field" color="danger">{`*Please generate the template`}</div>)
    }
    else {
      if (value?.prompt && value?.prompt?.length > 0) {
        return <></>
      } else return (<div className="error-field" color="danger">{`*Please enter the ${value?.type} prompt`}</div>)
    }
  }

  // display functions
  const displayApisForms = useCallback(() => {
    return selectedApis?.map((each) => (
      <FormGroup className="api-switch-form" switch key={each?.type}>
        <Input
          className="api-switch-input"
          type="switch"
          checked={
            selectedApis?.find((api) => api?.type === each?.type)?.status
          }
          disabled={disableCheck(each)}
          onClick={() => handleSwitch(each)}
        />
        <Label className="api-switch-label" check>
          {text_convert_lower(each?.type)}
        </Label>
        { getError(each) }
      </FormGroup>
    ));
  }, [selectedApis,attributes]);

  const displayPromptForms = useCallback(
    () =>
      selectedPrompts && selectedPrompts?.length > 0 ? (
        selectedPrompts?.map((each) => {
          const valAvailable = selectedPrompts?.find(
            (each2) => each2?.prompt === each?.prompt
          )?.status;
          return (
            <div
              key={each?.prompt}
              className={`filter-item ${valAvailable ? "active" : ""}`}
              onClick={() => handlePromptSelection(each)}
            >
              {each?.prompt}
              {valAvailable ? (
                <span
                  className="filter-close"
                  onClick={() => handlePromptSelection(each)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </span>
              ) : (
                <></>
              )}
            </div>
          );
        })
      ) : (
        <div className="not-found">No prompts selected</div>
      ),
    [selectedPrompts]
  );

  const [previousId, setPreviousId] = useState();
  const copyToClipboard = (text:string, id:any) => {
    if (text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      var element = document.getElementById(id);
      if (element) {
        // Check current className and toggle between red and blue
        element.classList.toggle('copied-color');
      }

    // alert('Copied to clipboard!');
    }
    
  };

  const unique_kdb = getFromSession(KB_NAMES_ENUM.UNIQUE_KDB);

  const project_main = useMemo(() => {
    const project_main_raw = getFromSession("project_main");
    if (project_main_raw) return JSON.parse(project_main_raw);
    else return "";
  }, []);
  
  const standeredApiList = [
    { id: 1, content: {project_id:project_main?.project_id} },
    { id: 2, content: {project_id:project_main?.project_id,unique_kdb:unique_kdb} },
    { id: 3, content: {project_id:project_main?.project_id,unique_kdb:unique_kdb} },
    { id: 4, content: {project_id:project_main?.project_id,unique_kdb:unique_kdb} },
  ];

  const displayContent = (content:any) =>{
   return <>
      {Object.keys(content).map((ele:any)=>(<>
            <div className="context">
              <div className="key">{ele}:</div><div className="value">{content[ele]}</div>
            </div>
      </>))}
     </>
  }

  const [expandedId, setExpandedId] = useState(null);
  const [expandedId2, setExpandedId2] = useState(null);

  const toggleAccordion = (id:any) => {
    setExpandedId((prevId) => (prevId === id ? null : id));
  };
  const toggleAccordion2 = (id:any) => {
    setExpandedId2((prevId) => (prevId === id ? null : id));
  };

  const displayApisList = useCallback(() => {
    return promtStatus && promtStatus?.length > 0 ? 
      <div className="prompts-list">
         <div className="prompts-item">
          <div className="heading">{text_convert_lower(APIS_LIST.DOC_AI_UPLOAD_FILE)}</div>
          { promtStatus?.find((each: any) => each?.[APIS_LIST.DOC_AI_UPLOAD_FILE]) ?
            <div>
              <div className="prompt" id="element0" >{`${basicUrl}/docai_upload_file`} &nbsp; <FontAwesomeIcon icon={faCopy} onClick={()=>copyToClipboard(`${basicUrl}/docai_upload_file`,'element0')} />
              &nbsp; &nbsp; &nbsp;<FontAwesomeIcon onClick={()=>toggleAccordion(standeredApiList[0]?.id)} icon={expandedId === standeredApiList[0]?.id ? faChevronDown : faChevronRight} /></div>
              <div className={`${expandedId === standeredApiList[0]?.id ? 'expanded' : 'closed'}`}>{displayContent(standeredApiList[0]?.content)}</div>
            </div> : 
            <div className="error-field" color="danger">{`*${APIS_LIST.DOC_AI_UPLOAD_FILE} api is not selected`}</div>
          }
        </div>
        <div className="prompts-item">
          <div className="heading">{text_convert_lower(APIS_LIST.VALIDATION)}</div>
          { promtStatus?.find((each: any) => each?.[APIS_LIST.VALIDATION]) ?
          <div>
            <div className="prompt" id="element1" >{`${basicUrl}/docai_doc_validation`} &nbsp; <FontAwesomeIcon icon={faCopy} onClick={()=>copyToClipboard(`${basicUrl}/docai_doc_validation`,'element1')} />
            &nbsp; &nbsp; &nbsp;<FontAwesomeIcon onClick={()=>toggleAccordion(standeredApiList[1]?.id)} icon={expandedId === standeredApiList[1]?.id ? faChevronDown : faChevronRight} /></div>
            <div className={`${expandedId === standeredApiList[1]?.id ? 'expanded' : 'closed'}`}>{displayContent(standeredApiList[1]?.content)}</div>
          </div>
             : 
            <div className="error-field" color="danger">{`*${APIS_LIST.VALIDATION} api is not selected`}</div>
          }
        </div>
        <div className="prompts-item">
          <div className="heading">{text_convert_lower(APIS_LIST.SUMMARIZATION)}</div>
          { promtStatus?.find((each: any) => each?.[APIS_LIST.SUMMARIZATION]) ?
          <div>
            <div className="prompt" id="element2">{`${basicUrl}/docai_kdb_summarization`} &nbsp; <FontAwesomeIcon icon={faCopy} onClick={()=>copyToClipboard(`${basicUrl}/docai_kdb_summarization`,'element2')} />
            &nbsp; &nbsp; &nbsp;<FontAwesomeIcon onClick={()=>toggleAccordion(standeredApiList[2]?.id)} icon={expandedId === standeredApiList[2]?.id ? faChevronDown : faChevronRight} /></div>
            <div className={`${expandedId === standeredApiList[2]?.id ? 'expanded' : 'closed'}`}>{displayContent(standeredApiList[2]?.content)}</div>
          </div> : 
          <div className="error-field" color="danger">{`*${APIS_LIST.SUMMARIZATION} api is not selected`}</div>
          }
        </div>
        <div className="prompts-item">
          <div className="heading">{text_convert_lower(APIS_LIST.DATA_EXTRACTION)}</div>
          { promtStatus?.find((each: any) => each?.[APIS_LIST.DATA_EXTRACTION]) ?
          <div>
            <div className="prompt" id="element3">{`${basicUrl}/docai_production_keyinfo`} &nbsp; <FontAwesomeIcon icon={faCopy} onClick={()=>copyToClipboard(`${basicUrl}/docai_production_keyinfo`,'element3')} />
            &nbsp; &nbsp; &nbsp;<FontAwesomeIcon onClick={()=>toggleAccordion(standeredApiList[3]?.id)} icon={expandedId === standeredApiList[3]?.id ? faChevronDown : faChevronRight} /></div>
            <div className={`${expandedId === standeredApiList[3]?.id ? 'expanded' : 'closed'}`}>{displayContent(standeredApiList[3]?.content)}</div>
            </div> : 
            <div className="error-field" color="danger">{`*${APIS_LIST.DATA_EXTRACTION} api is not selected`}</div>
          }
        </div>
      </div> 
    : <div className="not-found">No prompts selected</div>;
  }, [promtStatus, attributes, expandedId]);

  const displayPromptList = useCallback(() => {
    const chatbot_prompts = promtStatus?.find((each: any) => Object.keys(each)?.includes('chatbot_prompts'))?.chatbot_prompts
    return chatbot_prompts && chatbot_prompts?.length > 0 ? 
      chatbot_prompts?.map((each: any,id:number) => {
        const promptKey = Object.keys(each)?.find(str => str?.includes('prompt'))
        if (promptKey) {
          return <div className="prompts-item">
          <div className="heading">{promptKey} : {each?.[promptKey]}</div>
          <div className="prompt"  id="element4">{`${basicUrl}/${promptKey}`} &nbsp; <FontAwesomeIcon icon={faCopy} onClick={()=>copyToClipboard(`${basicUrl}/${promptKey}`,'element4')} />
          &nbsp; &nbsp; &nbsp;<FontAwesomeIcon onClick={()=>toggleAccordion2(id)} icon={expandedId2 === id ? faChevronDown : faChevronRight} />
          <div className={`${expandedId2 === id ? 'expanded' : 'closed'}`}>{displayContent(standeredApiList[1]?.content)}</div>
          </div>

        </div>
        } else return <></>
      }) 
    : <div className="not-found">No prompts selected</div>;
  }, [promtStatus,expandedId2]);
  const { themeName } = useContext(ThemeContext);
  return (
    <Modal
      className={`project-modal api-modal ${themeName === ThemeTypes?.BLUE_THEME ? '' : 'dark-modal'}`}
      isOpen={apiModal}
      toggle={toggleAPIModal}
    >
      <ModalHeader toggle={toggleAPIModal}>API Settings</ModalHeader>
      <ModalBody>
        <div className={`apis-list ${status === ProjectStatus.PRODUCTION ? 'prod' : ''}`}>
          <div className="standard">
            <div className="title">Standard API</div>
            <div className="apis">{(status === ProjectStatus.DRAFT || status === ProjectStatus.WORK_SPACE) ? displayApisForms() : displayApisList()}</div>
          </div>
          <div className="divider"></div>
          <div className="prompts">
            <div className="title">Prompt API</div>
            <div className="apis">
              <div className="filter-list">{(status === ProjectStatus.DRAFT || status === ProjectStatus.WORK_SPACE) ? displayPromptForms() : displayPromptList()}</div>
            </div>
          </div>
        </div>
        {(status === ProjectStatus.DRAFT || status === ProjectStatus.WORK_SPACE) ? <div className="btns">
          <Button className="cancel" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button
            className="submit"
            disabled={!selectedApis?.some(each => each?.status)}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </div> : <></>}
      </ModalBody>
    </Modal>
  );
};

export default ApiModal;
